import { injectIntl } from 'react-intl';
import { connect } from 'react-redux';
import { ApplicationState } from 'app/types-store';
import { createDescriptionSelector, textCollectionActionCreators } from '../translate';
import { withTranslation } from '../translate/with-translation';
import { invariant } from '../log/index';
import { bindActionCreators } from 'redux';
import { AppSetting } from 'app/startup/default/appsettings';
import { ShellState } from 'app/types-shared';

export interface TextMap { [index: string]: number }

/**
 * Return a object with a prop for each defined language code constant,
 * where the object property name corresponds to the constant name (instead
 * of the numeric language code)
 *
 * @param textSelector
 * @param translationMap Object map, or function returning an object map: (props) => objectmap
 * @param props
 */
// eslint:disable-next-line:one-variable-per-declaration
export const mapTextPropsFromState = (
  textSelector: (key: number) => string,
  translationMap: TextMap
) => {

    const textProps = {};

    Object.keys(translationMap).forEach((name) => {
      textProps[name] = textSelector(
        translationMap[name]
      ) || '';
    });
    return textProps;
};

export const mapSharedState = (state: ShellState) => ({
  ...state?.userInfo,
  locale: state?.locale,
  authState: state?.authState,
  settings: state?.settings
});

export const mapPropsFromSelectors = (injectSelectors, state, props) => {
  const selectorProps = {};
  if (injectSelectors) {
    Object.keys(injectSelectors).forEach((selector) => {
      selectorProps[selector] = injectSelectors[selector](state, props);
    });
  }
  return selectorProps;
};

export const mapPropsFromTranslations = (injectTranslations, texts) => {
  let textProps = {};
  if(injectTranslations){
    textProps = mapTextPropsFromState(
      createDescriptionSelector(texts),
      injectTranslations
    );
  }
  return textProps;
};

/**
 * Connects a component using redux `connect()`, plus:
 * - connects to the slice of state given by 'statePath' static property of component
 * - adding translated language texts as individual component props
 * - adds application shared state as `props.sharedState`
 */
export function containerFactory<TProps>(
  component: any,
  actionCreators: any = {},
  injectSelectors: any = {},
  injectTranslations: TextMap | ((props: unknown) => TextMap) = {}): React.ComponentType<React.PropsWithChildren<TProps>> {

  //TODO: deprecate the usage of static `statePath`. We want to move forward in a pure/functional direction and
  // remove the requirement of class usage. statePath might just as well be passed in as parameter, to remove the coupling
  // of a component to a specific state slice
  invariant(component.statePath, `Container component ${component.name} must define a static property named "statePath"`);

  const stateToProps = (state: ApplicationState, props: any) => {

    const componentState = state[component.statePath];
    const appsettingsTexts = AppSetting.Translations[component.statePath] as any;

    // Not throwing errors risks sending the application into a infinite loop
    invariant( !!componentState,
      `The state slice "${component.statePath}" does not exist.
      Most probably you forgot to set default reducer state or forgot to return state in reducer.`);

    const stateAndSelectorProps = {

      statePath: component.statePath,

      sharedState: mapSharedState(state.app),

      ...componentState,

      ...mapPropsFromSelectors(injectSelectors, componentState, props)

    };

    const mergedProps = {...props, ...stateAndSelectorProps} ;
    const translationsToInject = typeof injectTranslations === 'function'
      ? injectTranslations(mergedProps)
      : injectTranslations;

    return {
      ...stateAndSelectorProps,
      translation: {
        ...appsettingsTexts,
        ...mapPropsFromTranslations(translationsToInject, stateAndSelectorProps?.texts)
      }
    };
  };

  const mapDispatchToProps = (dispatch: any) => {
    const actions = { ...actionCreators, ...textCollectionActionCreators };
    return { dispatch, ...bindActionCreators(actions, dispatch) };
  };

  const target: any = injectIntl(component);

  const areaComponent = withTranslation(target, component.statePath);

  return connect(
    stateToProps,
    mapDispatchToProps
  )(areaComponent);
}
